import { useEffect, useRef, useState } from "react";
import { useLang } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { Sparkles, Send, UserRound } from "lucide-react";
import { toast } from "sonner";

export default function AIAssistant() {
  const { t } = useLang();
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/history`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then((msgs) => setMessages(msgs))
      .catch(() => {});
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ message: msg, session_id: `ai_${user?.user_id}` }),
      });
      if (!res.ok) throw new Error("AI request failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          try {
            const data = JSON.parse(part.slice(6));
            if (data.delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + data.delta };
                return copy;
              });
            }
          } catch {}
        }
      }
    } catch {
      toast.error("AI unavailable");
      setMessages((m) => m.slice(0, -1));
    } finally {
      setStreaming(false);
    }
  };

  const suggestions = [t("ai.q1"), t("ai.q2"), t("ai.q3"), t("ai.q4")];

  return (
    <div className="flex flex-col h-[calc(100vh-8.5rem)] max-w-3xl mx-auto" data-testid="ai-page">
      <div className="flex items-center gap-3 pb-4 border-b border-sand-border">
        <div className="w-11 h-11 rounded-2xl bg-brand-950 flex items-center justify-center">
          <Sparkles className="w-5 h-5 text-gold-500" />
        </div>
        <div>
          <h1 className="font-sans text-xl font-bold text-slate-900 tracking-tight">{t("ai.title")}</h1>
          <p className="text-xs text-muted-foreground">{t("ai.sub")}</p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto py-5 space-y-4" data-testid="ai-messages">
        {messages.length === 0 && (
          <div className="text-center py-10">
            <div className="w-14 h-14 rounded-2xl bg-gold-500/10 border border-gold-500/30 flex items-center justify-center mx-auto mb-4">
              <Sparkles className="w-7 h-7 text-gold-600" />
            </div>
            <p className="text-sm text-muted-foreground mb-5">Assalamualaikum. Ask me anything about your agency's live data.</p>
            <div className="flex flex-wrap justify-center gap-2">
              {suggestions.map((q) => (
                <button key={q} data-testid={`ai-suggestion-${suggestions.indexOf(q)}`} onClick={() => send(q)}
                  className="text-xs px-4 py-2 rounded-full bg-white border border-sand-border hover:border-gold-500/50 hover:text-gold-700 transition-colors">
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex gap-3 ${m.role === "user" ? "justify-end" : ""}`} data-testid={`ai-message-${i}`}>
            {m.role === "assistant" && (
              <div className="w-8 h-8 rounded-xl bg-brand-950 flex items-center justify-center shrink-0 mt-0.5">
                <Sparkles className="w-4 h-4 text-gold-500" />
              </div>
            )}
            <div className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-brand-800 text-sand rounded-br-md" : "bg-white border border-sand-border text-brand-950 rounded-bl-md"}`}>
              {m.content || (streaming && i === messages.length - 1 ? <span className="inline-block w-2 h-4 bg-gold-500 animate-pulse rounded-sm" /> : "")}
            </div>
            {m.role === "user" && (
              <div className="w-8 h-8 rounded-xl bg-gold-500/15 border border-gold-500/30 flex items-center justify-center shrink-0 mt-0.5">
                <UserRound className="w-4 h-4 text-gold-700" />
              </div>
            )}
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="pt-4 border-t border-sand-border flex gap-2">
        <input data-testid="ai-chat-input" value={input} onChange={(e) => setInput(e.target.value)}
          placeholder={t("ai.placeholder")} disabled={streaming}
          className="flex-1 h-12 px-5 rounded-full bg-white border border-sand-border text-sm outline-none focus:border-gold-500 transition-colors disabled:opacity-50" />
        <button data-testid="ai-chat-submit-button" type="submit" disabled={streaming || !input.trim()}
          className="w-12 h-12 rounded-full bg-brand-950 hover:bg-brand-800 disabled:opacity-40 flex items-center justify-center transition-colors">
          <Send className="w-4.5 h-4.5 w-[18px] h-[18px] text-gold-500" />
        </button>
      </form>
    </div>
  );
}
